import React, { useEffect, useRef, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { getToken, onMessageListener } from "../firebase/firebase-messaging";
import { isNewNotificationSupported } from "common/helpers";
import { updateNotificationInteraction } from "../api/messaging";
import { myFirebase } from "../firebase/firebase";

const PushNotification = ({ storeDeviceToken, isAuthenticated, callInProgress, callRoom }) => {
  const [message, setMessage] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [isSilent, setIsSilent] = useState(false);
  const ringtone = useRef(null);
  const call = useRef();
  call.current = { callInProgress, callRoom };

  useEffect(() => {
    if (isNewNotificationSupported() && isAuthenticated) {
      getToken(storeDeviceToken);
    }
  }, [isAuthenticated]);

  const stopRingtone = () => {
    if (ringtone.current) {
      ringtone.current.pause();
      ringtone.current = null;
    }
  };

  useEffect(() => {
    const unsubscribe = onMessageListener(payload => {
      const notifId = payload?.data?.notification_id;
      if (notifId) {
        console.log("✅ [NOTIFICATION] notification_id extracted:", notifId);
      } else {
        console.warn("⚠️ [NOTIFICATION] No notification_id found in payload");
      }

      if (payload?.data?.type !== "incoming_call") {
        setMessage(payload);
        return;
      }

      const { callInProgress, callRoom } = call.current;
      // Everyone who presses call in a chat rings the others again, so the call
      // already open here can ring itself.
      if (callInProgress && payload.data.room === callRoom) {
        return;
      }

      setIncomingCall(payload);
      stopRingtone();
      setIsSilent(callInProgress);
      if (!callInProgress) {
        const audio = new Audio("/ringtone.mp3");
        audio.loop = true;
        // Browsers block sound on a page the user hasn't clicked yet; the call box still shows.
        audio.play().catch(() => {});
        ringtone.current = audio;
      }
    });
    return () => {
      unsubscribe();
      stopRingtone();
    };
  }, []);

  const trackNotificationInteraction = async (payload, interactionType) => {
    const notificationId = payload?.data?.notification_id;
    const notificationType = payload?.data?.type;

    if (!notificationId) {
      console.warn("⚠️ [NOTIFICATION TRACKING] No notification_id available, skipping tracking");
      return;
    }

    if (!isAuthenticated) {
      console.warn("⚠️ [NOTIFICATION TRACKING] User not authenticated, skipping tracking");
      return;
    }

    // Determine if this is a chat notification (chat or incoming_call)
    const isChatNotification = notificationType === 'chat' || notificationType === 'incoming_call';
    const endpoint = isChatNotification
      ? `/api/v1/notifications/chat/${notificationId}/`
      : `/api/v1/notifications/events/${notificationId}/`;

    try {
      const idToken = myFirebase.auth().currentUser
        ? await myFirebase.auth().currentUser.getIdToken()
        : null;

      if (idToken) {
        const auth = { idToken };
        const response = await updateNotificationInteraction(notificationId, interactionType, auth, isChatNotification);

        console.log("✅ [NOTIFICATION TRACKING] API call successful:", response);
      } else {
        console.warn("⚠️ [NOTIFICATION TRACKING] No ID token available, skipping API call");
      }
    } catch (error) {
      console.error("❌ [NOTIFICATION TRACKING] Failed to track notification interaction:", error);
      console.error("❌ [NOTIFICATION TRACKING] Error details:", {
        notificationId,
        notificationType,
        isChatNotification,
        interactionType,
        error: error.message,
        stack: error.stack
      });
    }
  };

  const handleAcceptCall = async () => {
    console.log("📞 [NOTIFICATION] Call ACCEPTED by user");
    stopRingtone();

    // Track notification as opened
    await trackNotificationInteraction(incomingCall, "opened");

    setIncomingCall(null);
    window.location.href = `${window.location.origin}/chat`;
  };

  const handleDeclineCall = async () => {
    stopRingtone();

    // Track notification as dismissed
    await trackNotificationInteraction(incomingCall, "dismissed");

    setIncomingCall(null);
  };

  // Helper function to parse deep link and navigate
  const handleDeepLink = (deepLink) => {
    if (!deepLink) return null;


    // Handle mentorship://open/mentorshipchat/<room_id>
    const mentorshipChatMatch = deepLink.match(/^mentorship:\/\/open\/mentorshipchat\/(\d+)$/);
    if (mentorshipChatMatch) {
      const roomId = mentorshipChatMatch[1];
      // Convert to web URL with channel_id query param
      const webUrl = `/chat?channel_id=${roomId}`;
      return webUrl;
    }

    return null;
  };

  const handleNotificationClick = async () => {
    // Track notification as opened
    await trackNotificationInteraction(message, "opened");

    // Handle navigation from deep link or path
    const notificationId = message?.data?.notification_id;
    const path = message?.data?.path || message?.data?.deeplink;
    if (path) {
      if (path.startsWith('mentorship://')) {
        const webPath = handleDeepLink(path);
        if (webPath) {
          // Add notification_id to URL if available
          const finalPath = notificationId
            ? `${webPath}${webPath.includes('?') ? '&' : '?'}notification_id=${notificationId}`
            : webPath;
          window.location.href = finalPath;
          setMessage(null);
          return;
        }
      } else if (path.startsWith('http://') || path.startsWith('https://')) {
        // External URL
        window.location.href = path;
        setMessage(null);
        return;
      } else if (path.startsWith('/')) {
        // Internal path
        const finalPath = notificationId
          ? `${path}${path.includes('?') ? '&' : '?'}notification_id=${notificationId}`
          : path;
        window.location.href = finalPath;
        setMessage(null);
        return;
      }
    }

    // Default: just close notification
    setMessage(null);
  };

  const handleNotificationClose = async () => {
    // Track notification as dismissed
    await trackNotificationInteraction(message, "dismissed");
    setMessage(null);
  };

  useEffect(() => {
    if (!message) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

    // FCM nests what is shown under `notification`; `data` carries the payload
    // the click handler reads.
    const {title, body, icon} = message.notification || {};

    let instance;
    try {
      instance = new Notification(title, {body, icon, data: message.data});
    } catch (e) {
      console.log('Notification onError', e);
      return;
    }
    instance.onshow = () => console.log('✅ [NOTIFICATION] WebNotification shown to user');
    instance.onclick = handleNotificationClick;
    instance.onclose = handleNotificationClose;
    instance.onerror = (e) => console.log('Notification onError', e);

    // Detached first: closing here would otherwise re-enter handleNotificationClose.
    return () => {
      instance.onshow = null;
      instance.onclick = null;
      instance.onclose = null;
      instance.onerror = null;
      instance.close();
    };
  }, [message]);

  return (
    <>
      <Snackbar
        open={!!incomingCall && isSilent}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={10000}
        onClose={(event, reason) => reason === "timeout" && setIncomingCall(null)}
      >
        <Alert severity="info" onClose={handleDeclineCall}>
          <strong>{incomingCall?.notification?.title || "Incoming Call"}</strong>
          <div>{incomingCall?.notification?.body}</div>
        </Alert>
      </Snackbar>
      {incomingCall && !isSilent ? (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999
        }}>
          <div style={{
            background: "white",
            padding: "20px",
            borderRadius: "10px",
            textAlign: "center",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)"
          }}>
            <p>{incomingCall.notification?.title || "Incoming Call"}</p>
            <button onClick={handleAcceptCall} style={{
              margin: "10px",
              padding: "10px 20px",
              fontSize: "16px",
              cursor: "pointer",
              backgroundColor: "green",
              color: "white",
              border: "none",
              borderRadius: "5px"
            }}>Accept</button>
            <button onClick={handleDeclineCall} style={{
              margin: "10px",
              padding: "10px 20px",
              fontSize: "16px",
              cursor: "pointer",
              backgroundColor: "red",
              color: "white",
              border: "none",
              borderRadius: "5px"
            }}>Decline</button>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default PushNotification;
